import React, { useMemo, useState } from 'react';
import {
	PaginationNavigator,
	ToggleButtons,
	SearchInput,
	usePaginatorStore,
	paginatorStoreSelector,
} from '@warehouse/shared/ui';
import styled, { css } from 'styled-components';
import { TitleFull, TitleLightWithSelectedParent } from '@warehouse/title/core';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import { LoaderWrapper } from '../../../src/components/library/LoaderWrapper';
import InheritanceOnValueChangeSimpleTableWrapper from '../../../src/components/library/SimpleTable/Wrappers/InheritanceOnValueChangeSimpleTableWrapper';
import { fromBasic, getTitleData } from '../../../src/utils/titleGetProperty';
import { ChildrenParentMode } from './children-parent-mode';
import { CreateRelationshipsButton } from './CreateRelationshipsButton';
import { useRelationshipColumns } from './useRelationshipColumns';
import { useSearchRelationshipsParentsOrChildren } from '../domain/useSearchRelationshipsParentsOrChildren';
import { RelationshipListStoreConfig } from './relationship-list-store-config';
import { listRelationshipStoreSelector, useListRelationshipStore } from './list-relationship.store';
import { AddRelationshipChildrenPrompt } from '../feature-add-relationship-children';

function RelationshipsListComp({ title, onChildrenClick }: RelationshipsListProps) {
	const childrenParentMode = useListRelationshipStore(listRelationshipStoreSelector.mode);
	const { setMode: setChildrenParentMode } = useListRelationshipStore(listRelationshipStoreSelector.actions);
	const { setPage } = usePaginatorStore(paginatorStoreSelector.actions);

	const [openExistingChildrenModal, setOpenExistingChildrenModal] = useState(false);

	const titleStatus = useMemo(
		() => getTitleData<string>(title, fromBasic(['terms', 'titleStatus', 'displayValue'])),
		[title],
	);

	const columns = useRelationshipColumns(childrenParentMode);
	const { data, loading } = useSearchRelationshipsParentsOrChildren({
		titleUuid: title.uuid,
		workType: title.readonly.workType,
		mode: childrenParentMode,
		showHiddenTitles: titleStatus === 'hidden',
	});

	const isTitleHidden = useTitleEditorStore(titleEditorStoreSelector.isTitleHidden);

	if (!title.readonly) {
		return null;
	}

	return (
		<Wrapper>
			<ToggleButtonRow>
				<ToggleButtons<ChildrenParentMode>
					value={childrenParentMode}
					onChange={(value) => {
						setChildrenParentMode(value);
						setPage(1);
					}}
					options={[
						{ value: 'parents', label: 'Parents' },
						{ value: 'children', label: 'Children' },
					]}
				/>
			</ToggleButtonRow>
			<TopRow>
				<SearchInput label={childrenParentMode} />
				<CreateRelationshipsButton
					workType={title.readonly.workType}
					childrenParentMode={childrenParentMode}
					openExistingChildrenModal={() => setOpenExistingChildrenModal(true)}
					parentUuid={title.uuid}
				/>
			</TopRow>
			<LoaderWrapper loading={loading} opacity={0.3} style={{ flex: 1 }}>
				<InheritanceOnValueChangeSimpleTableWrapper<TitleLightWithSelectedParent>
					ariaLabel={`${childrenParentMode} relationships`}
					paddedCells
					columns={columns}
					editMode={false}
					data={data}
					tableStyle="border"
					forceHover
					onClickRow={(row: TitleLightWithSelectedParent) => onChildrenClick?.(childrenParentMode, row)}
				/>
			</LoaderWrapper>
			<AddRelationshipChildrenPrompt
				open={openExistingChildrenModal}
				onClose={() => setOpenExistingChildrenModal(false)}
				title={title}
				showHiddenTitles={isTitleHidden}
				textHeader="Add Existing Children"
				searchAndSelectedPluralLabel="Titles"
			/>
			<PaginationNavigator centered />
		</Wrapper>
	);
}

export function RelationshipsList({ ...props }: RelationshipsListProps) {
	return (
		<RelationshipListStoreConfig>
			<RelationshipsListComp {...props} />
		</RelationshipListStoreConfig>
	);
}

interface RelationshipsListProps {
	title: TitleFull;
	onChildrenClick?: (mode: ChildrenParentMode, child: TitleLightWithSelectedParent) => void;
}

const Wrapper = styled.div(
	({ theme }) => css`
		display: flex;
		flex-direction: column;
		gap: 16px;
		height: calc(100% - 32px);
		padding: ${theme.spacing(2)};
	`,
);

const TopRow = styled.div(
	({ theme }) => css`
		align-items: center;
		display: flex;
		gap: ${theme.spacing(2)};
		width: 100%;
	`,
);

const ToggleButtonRow = styled.div(
	() => css`
		display: flex;
		justify-content: center;
		margin-top: 16px;
	`,
);
